/**
 * WidgetNode — renders a custom element for a board item that has a
 * `widget` field configured.
 *
 * - The element tag comes from `widget` on the resolved module body.
 * - Every `widget.<name>` field becomes a kebab-cased HTML attribute.
 * - The full module data (and connection list when available) is set as
 *   a DOM property on the element so widget authors can read structured
 *   objects via `el.arch` / `el.archConnections` instead of parsing
 *   stringified attributes.
 * - The element is positioned to fill the item's wrapper div via CSS;
 *   widgets size themselves with `:host { width: 100%; height: 100%; }`
 *   inside their shadow root.
 */
import { createElement, useCallback, useEffect, useMemo, useRef } from "react";
import type { Module } from "@archlang/scene/arch-model";
import { paletteColor } from "@archlang/scene";
import {
  applyWidgetTemplate,
  COLOR_PROPS,
  humanizeLabel,
  moduleKey,
  propNameToAttr,
  propValueToAttr,
  type WidgetSpec,
} from "@archlang/scene/arch-model";
import { useNodeColoring } from "./coloring/useNodeColoring.js";
import { useLifecyclePaint } from "./lifecycle/useLifecycleState.js";
import { bandCssVars } from "./lodBands.js";

export interface WidgetNodeProps {
  readonly tag: string;
  readonly spec: WidgetSpec;
  /** The module backing this item — set as `el.arch` for the widget. */
  readonly module: Module;
  /** Optional connection summary (in/out edges). Set as `el.archConnections`. */
  readonly connections?: { incoming: unknown[]; outgoing: unknown[] };
  /** Continuous scale (1 = model space; >1 zoomed in; <1 zoomed out). */
  readonly scale?: number;
  /** The item's placed size in model px. Stamped as `width`/`height` attrs
   *  (unless the author set `widget.width`/`widget.height`, which the solver
   *  already honored) so size-aware widgets — the scene card's proportional
   *  token scaling — compose at the node's true dimensions. */
  readonly itemWidth?: number;
  readonly itemHeight?: number;
  /** Whether this item is the currently-selected module. */
  readonly selected?: boolean;
  /** Whether this item is in the active blast radius / highlight set. */
  readonly highlighted?: boolean;
  /** Whether the pointer is currently over this item's bounding box. */
  readonly hovered?: boolean;
  /**
   * True when the rendered item carries a non-empty nested space — i.e.
   * the renderer is about to mount a subspace overlay over this widget.
   * Surfaced as `data-has-subspace="true"` on the widget host so widgets
   * can switch chrome modes (e.g. hide their description band) without
   * having to introspect `el.arch.children` (which carries phantom
   * children produced by partial parses).
   */
  readonly hasSubspace?: boolean;
  /**
   * Optional React children passed to the custom element as light-DOM
   * children. Widgets that contain nested module children (a `system`
   * with a children space, etc.) project these via a `<slot></slot>`
   * inside their shadow DOM. Widgets without a slot drop them — same
   * default behaviour as any HTMLElement that has unprojected light DOM.
   */
  readonly children?: React.ReactNode;
}

/**
 * Inspector-friendly: a stable role + the tag the widget claims, so the
 * inspector and tests can find the widget container without coupling to
 * the user-defined tag name.
 */
const WIDGET_HOST_CLASS = "arch-widget-host";

export function WidgetNode({
  tag,
  spec,
  module,
  connections,
  scale,
  itemWidth,
  itemHeight,
  selected,
  highlighted,
  hovered,
  hasSubspace,
  children,
}: WidgetNodeProps) {
  const ref = useRef<HTMLElement | null>(null);
  // Unified node colour (aspect/label or diff status) — see coloring/.
  // Applied as the widget accent (`--arch-module-accent`), the same stroke
  // mechanic label colouring uses, so every source renders identically.
  const coloring = useNodeColoring();
  const nodeColor = coloring?.colorForNode(moduleKey(module));
  const lifecycle = useLifecyclePaint()?.get(moduleKey(module));

  // Set DOM properties imperatively. React 18 doesn't reliably forward
  // arbitrary props to custom elements, so we go through a ref. State info
  // also goes onto a structured `el.archState` for JS-driven widgets that
  // need richer info than data-attrs allow. (An `el.archZoom` used to ride
  // along too — zero consumers ever materialised and stamping it kept
  // `scale` in these effect deps, so it was dropped.)

  // Set DOM properties imperatively. The ref callback fires synchronously
  // on append, before React's useEffect, so children mounted by the
  // element's connectedCallback can see `.arch` on first read.
  const initialPropsApplied = useRef(false);
  const refCallback = useCallback((el: HTMLElement | null) => {
    ref.current = el;
    if (!el) { initialPropsApplied.current = false; return; }
    (el as unknown as { arch?: unknown }).arch = module;
    if (connections) {
      (el as unknown as { archConnections?: unknown }).archConnections = connections;
    }
    (el as unknown as { archState?: unknown }).archState = {
      selected: !!selected,
      highlighted: !!highlighted,
      hovered: !!hovered,
    };
    initialPropsApplied.current = true;
  }, [module, connections, selected, highlighted, hovered]);

  // Subsequent updates after first mount: same writes, but trigger via
  // useEffect so React's render cycle drives them. The ref callback only
  // fires on element identity change (rare for kind widgets) — useEffect
  // covers prop-only changes.
  useEffect(() => {
    const el = ref.current;
    if (!el || !initialPropsApplied.current) return;
    (el as unknown as { arch?: unknown }).arch = module;
    if (connections) {
      (el as unknown as { archConnections?: unknown }).archConnections = connections;
    }
    (el as unknown as { archState?: unknown }).archState = {
      selected: !!selected,
      highlighted: !!highlighted,
      hovered: !!hovered,
    };
  }, [module, connections, selected, highlighted, hovered]);

  // Build the attribute set from the widget spec. We pass attrs through
  // React's `setAttribute` semantics so authors can use
  // `attributeChangedCallback` for reactive updates.
  const attrs: Record<string, string> = {};
  // v0.8.2 §5.1: display the humanized label; identity/refs stay raw (module.name).
  attrs["name"] = humanizeLabel(module.name);
  for (const [propName, propValue] of spec.props) {
    const value = propValueToAttr(propValue);
    // This is the live backend's peer of `moduleToCardInput`: the one place a
    // widget spec becomes DOM attributes, so it is where a palette NAME has to
    // become this theme's pixels. Resolving HERE rather than inside each widget
    // is what makes it hold for the widgets that cannot resolve it themselves —
    // the stdlib element bundles are plain JS with no access to the palette.
    attrs[propNameToAttr(propName)] = COLOR_PROPS.has(propName) ? paletteColor(value) : value;
  }
  // Placed size — authored widget.width/height (already in attrs from the
  // spec loop) win; otherwise the solver's placement is the truth.
  if (itemWidth != null && attrs["width"] == null) attrs["width"] = String(Math.round(itemWidth));
  if (itemHeight != null && attrs["height"] == null) attrs["height"] = String(Math.round(itemHeight));
  if (selected) attrs["data-selected"] = "true";
  if (highlighted) attrs["data-highlighted"] = "true";
  // `data-hovered` is set whenever the caller drives hover state (board
  // path). Setting "false" explicitly (not omitting) lets widget CSS
  // suppress its `:host(:hover)` fallback via `:not([data-hovered])`,
  // so the React state is the single source of truth for board nodes
  // (avoids sticky CSS :hover on GPU-composited layers in Chrome).
  if (hovered !== undefined) attrs["data-hovered"] = hovered ? "true" : "false";
  if (hasSubspace) attrs["data-has-subspace"] = "true";
  // Unified node colour. Set it three ways so every widget path honours it
  // as a STROKE: `--arch-module-accent` (CSS var, below) for var-driven
  // widgets; `data-aspect`/`data-colored` for the host ring CSS; and the
  // `color` attribute so the built-in module element's own accent resolver
  // (which reads `color`, not the var, and would otherwise reset the var to
  // its default) renders the unified colour. Overriding `color` is the same
  // "colour-by overrides widget.color" rule aspect colouring already used.
  if (nodeColor) {
    attrs["data-aspect"] = nodeColor;
    attrs["data-colored"] = "true";
    attrs["color"] = nodeColor;
  }
  // Lifecycle rides its OWN attributes rather than the colour channel: form
  // (dash / fade / strike / badge) carries lifecycle so hue stays free for
  // aspect and diff colouring, and the two read at the same time.
  if (lifecycle) {
    attrs["data-lifecycle"] = lifecycle.state;
    if (lifecycle.badge) attrs["data-lifecycle-badge"] = lifecycle.badge;
  }
  // `data-module-id` is exposed whenever the module has a stable id so the
  // ghost-links overlay (and other diff visualisers) can locate the host
  // node in the DOM via attribute selector regardless of widget tag.
  if (module.id) attrs["data-module-id"] = String(module.id);
  const className = WIDGET_HOST_CLASS;

  // Continuous LOD band signals. Widgets drive layout (flex-grow on
  // [header]/[body]/[details]) and styling from these. Frame-by-frame
  // smooth — no CSS transitions needed, the vars themselves change
  // each render as zoom shifts.
  const effScale = scale ?? 1;
  const bandVars = bandCssVars(effScale);

  // createElement avoids TypeScript's JSX intrinsic-element check for an
  // arbitrary tag name. The inline style stretches the widget to the
  // wrapper bounds; the item's div already controls position and size,
  // the widget just fills. CSS custom properties carry continuous
  // signals (zoom scale, projected pixel width, band progressions) —
  // useful for widgets that want non-stepwise visual changes via
  // `calc()` or grow expressions. Children are passed as the
  // third argument so the browser places them in the element's light
  // DOM, where the widget's shadow-root <slot> can project them.
  return createElement(
    tag,
    {
      ref: refCallback,
      className,
      style: {
        display: "block",
        width: "100%",
        height: "100%",
        ...bandVars,
        ...(nodeColor ? { "--arch-module-accent": nodeColor } : {}),
      },
      ...attrs,
    },
    children
  );
}

/**
 * Inline-HTML template node. Used when a module has `widget: "<html>"`
 * (string-form) — we render the template string into a plain div via
 * dangerouslySetInnerHTML.
 *
 * The template's `{{path}}` placeholders are pre-resolved against the
 * module's fields/labels and HTML-escaped (`{{x}}`) or kept raw
 * (`{{{x}}}`). Re-resolving on every render is cheap and keeps the
 * output reactive to model changes; the result is memoised on the
 * (template, module) pair so identical inputs don't re-do the work.
 */
export interface TemplateNodeProps {
  readonly template: string;
  readonly module: Module;
  readonly scale?: number;
  readonly selected?: boolean;
  readonly highlighted?: boolean;
  readonly hovered?: boolean;
}

const TEMPLATE_HOST_CLASS = "arch-widget-template";

export function TemplateNode({
  template,
  module,
  scale,
  selected,
  highlighted,
  hovered,
}: TemplateNodeProps) {
  const ref = useRef<HTMLElement | null>(null);
  const coloring = useNodeColoring();
  const nodeColor = coloring?.colorForNode(moduleKey(module));
  const lifecycle = useLifecyclePaint()?.get(moduleKey(module));
  const html = useMemo(
    () =>
      applyWidgetTemplate(template, {
        name: humanizeLabel(module.name),
        typeName: module.typeName,
        description: module.description,
        fields: module.fields,
        aspects: module.aspects,
      }),
    [template, module]
  );

  // Set the model BEFORE the element is connected so primitives in the
  // template see it on their very first connectedCallback. The ref
  // callback fires synchronously on append, before React's useEffect.
  const refCallback = useCallback((el: HTMLElement | null) => {
    ref.current = el;
    if (!el) return;
    (el as unknown as { arch?: unknown }).arch = module;
    (el as unknown as { setTemplate?: (s: string) => void }).setTemplate?.(html);
  }, [module, html]);

  // Subsequent updates: rewrite shadow contents + re-fire model event.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    (el as unknown as { arch?: unknown }).arch = module;
    (el as unknown as { setTemplate?: (s: string) => void }).setTemplate?.(html);
  }, [html, module]);

  const effScale = scale ?? 1;
  const bandVars = bandCssVars(effScale);

  return createElement("arch-template", {
    ref: refCallback,
    className: TEMPLATE_HOST_CLASS,
    "data-selected": selected ? "true" : undefined,
    "data-highlighted": highlighted ? "true" : undefined,
    "data-hovered": hovered === undefined ? undefined : hovered ? "true" : "false",
    "data-colored": nodeColor ? "true" : undefined,
    "data-aspect": nodeColor,
    "data-lifecycle": lifecycle?.state,
    "data-lifecycle-badge": lifecycle?.badge,
    style: {
      display: "block",
      width: "100%",
      height: "100%",
      ...bandVars,
      ...(nodeColor ? { "--arch-module-accent": nodeColor } : {}),
    },
  });
}
